import type { AccountInput, IncomeSourceInput, ObligationInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  Card,
  FormField,
  PageHeader,
  SelectInput,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { ProfileRead, PurposeRead, SettingsRead } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { QueryState } from "../components/QueryState";
import { formatMoney, parseMoneyInput, titleFromMachineValue } from "../lib/format";

const steps = [
  "profile",
  "accounts",
  "purposes",
  "income",
  "obligations",
  "rules",
  "spark",
  "briefing",
] as const;

type OnboardingStep = (typeof steps)[number];

const stepTitles: Record<OnboardingStep, string> = {
  profile: "Your household",
  accounts: "Add your accounts",
  purposes: "Give money a job",
  income: "Confirm income",
  obligations: "Add obligations",
  rules: "Protect the essentials",
  spark: "Set up Spark",
  briefing: "Your first briefing",
};

function isOnboardingStep(value: string | undefined): value is OnboardingStep {
  return steps.includes(value as OnboardingStep);
}

function isIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

function onboardingTimeZone(savedTimeZone: string): string {
  return isIanaTimeZone(savedTimeZone)
    ? savedTimeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function OnboardingLayout({ step, children }: { step: OnboardingStep; children: React.ReactNode }) {
  const { signOut } = useAuth();
  const index = steps.indexOf(step);
  const percent = ((index + 1) / steps.length) * 100;
  return (
    <main className="onboarding-shell">
      <header className="onboarding-header">
        <Link className="brand-mark" to="/" aria-label="IterNest Money">
          <span className="brand-mark__map" aria-hidden="true">
            I
          </span>
          <span>IterNest Money</span>
        </Link>
        <Button variant="quiet" type="button" onClick={() => void signOut()}>
          Save and leave
        </Button>
      </header>
      <div
        className="onboarding-progress"
        aria-label={"Setup step " + String(index + 1) + " of " + String(steps.length)}
        aria-valuemax={steps.length}
        aria-valuemin={1}
        aria-valuenow={index + 1}
        role="progressbar"
      >
        <div className="onboarding-progress__label">
          <span>
            Step {index + 1} of {steps.length}
          </span>
          <span>{stepTitles[step]}</span>
        </div>
        <div className="onboarding-progress__track" aria-hidden="true">
          <span style={{ width: String(percent) + "%" }} />
        </div>
      </div>
      {children}
    </main>
  );
}

function SaveError({ error }: { error: Error | null }) {
  return error ? (
    <Callout title="That was not saved" tone="urgent" role="alert">
      {error.message}
    </Callout>
  ) : null;
}

function ContinueButton({
  isLoading,
  children = "Save and continue",
  disabled,
  onClick,
}: {
  isLoading: boolean;
  children?: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      type={onClick ? "button" : "submit"}
      onClick={onClick}
      isLoading={isLoading}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}

function ProfileStep({ profile }: { profile: ProfileRead }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState } = useForm<{
    displayName: string;
    householdName: string;
    timezone: string;
    baseCurrency: string;
    privacyAccepted: boolean;
  }>({
    defaultValues: {
      displayName: profile.displayName ?? "",
      householdName: profile.household.name,
      timezone: onboardingTimeZone(profile.timezone),
      baseCurrency: profile.household.baseCurrency || "USD",
      privacyAccepted: false,
    },
  });
  const mutation = useMutation({
    mutationFn: (data: {
      displayName: string;
      householdName: string;
      timezone: string;
      baseCurrency: string;
      privacyAccepted: boolean;
    }) =>
      api.saveOnboardingProgress({
        step: "accounts",
        data,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/onboarding/accounts");
    },
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="A calm starting point"
        title="Set the household clock"
        description="Dates, paychecks, and life days follow your household timezone. IterNest Money supports one base currency in Version 1."
      />
      <Card>
        <form className="form-stack" onSubmit={handleSubmit((data) => mutation.mutate(data))}>
          <div className="form-grid form-grid--two">
            <FormField
              label="Your name"
              htmlFor="display-name"
              error={formState.errors.displayName?.message}
            >
              <TextInput
                id="display-name"
                autoComplete="name"
                {...register("displayName", { required: "Enter your name." })}
              />
            </FormField>
            <FormField
              label="Household name"
              htmlFor="household-name"
              error={formState.errors.householdName?.message}
            >
              <TextInput
                id="household-name"
                {...register("householdName", { required: "Name this household." })}
              />
            </FormField>
            <FormField
              label="Timezone"
              htmlFor="timezone"
              hint="Used for due dates, briefings, and life days."
            >
              <TextInput
                id="timezone"
                autoComplete="off"
                {...register("timezone", {
                  required: "Choose a timezone.",
                  validate: (value) =>
                    isIanaTimeZone(value) || "Use a timezone such as America/Denver.",
                })}
              />
            </FormField>
            <FormField label="Base currency" htmlFor="base-currency">
              <SelectInput id="base-currency" {...register("baseCurrency")}>
                <option value="USD">USD — US dollar</option>
                <option value="CAD">CAD — Canadian dollar</option>
              </SelectInput>
            </FormField>
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              {...register("privacyAccepted", {
                required: "Acknowledge how IterNest Money uses household data.",
              })}
            />
            <span>
              I understand IterNest Money stores the financial information I enter to calculate and
              explain my household plan.
            </span>
          </label>
          {formState.errors.privacyAccepted ? (
            <p className="field-error">{formState.errors.privacyAccepted.message}</p>
          ) : null}
          <SaveError error={mutation.error} />
          <div className="button-row button-row--end">
            <ContinueButton isLoading={mutation.isPending} />
          </div>
        </form>
      </Card>
    </div>
  );
}

type AccountForm = {
  name: string;
  type: AccountInput["type"];
  currentBalance: string;
  availableBalance: string;
  protectionLevel: AccountInput["protectionLevel"];
};

function AccountsStep({ profile }: { profile: ProfileRead }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const { register, handleSubmit, reset, formState } = useForm<AccountForm>({
    defaultValues: {
      type: "checking",
      currentBalance: "",
      availableBalance: "",
      protectionLevel: "standard",
    },
  });
  const createMutation = useMutation({
    mutationFn: (input: AccountInput) => api.createAccount(input),
    onSuccess: async () => {
      reset();
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
  const continueMutation = useMutation({
    mutationFn: () => api.saveOnboardingProgress({ step: "purposes", data: {} }),
    onSuccess: () => navigate("/onboarding/purposes"),
  });

  const submitAccount = handleSubmit((values) => {
    setFormError(undefined);
    const currentBalanceMinor = parseMoneyInput(values.currentBalance);
    const availableBalanceMinor = values.availableBalance
      ? parseMoneyInput(values.availableBalance)
      : null;
    if (
      currentBalanceMinor === null ||
      (values.availableBalance && availableBalanceMinor === null)
    ) {
      setFormError("Enter balances as dollars and cents, for example 1250.00 or -42.18.");
      return;
    }
    createMutation.mutate({
      name: values.name,
      type: values.type,
      currentBalanceMinor,
      ...(availableBalanceMinor !== null ? { availableBalanceMinor } : {}),
      currency: profile.household.baseCurrency,
      protectionLevel: values.protectionLevel,
    });
  });

  const accounts = accountsQuery.data?.data ?? [];
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Manual-first Version 1"
        title="Add the accounts that carry your plan"
        description="Use the current balance shown by your bank. IterNest Money never treats a credit limit as cash."
      />
      <Callout title="How accounts arrive in IterNest Money" tone="info">
        Add account details manually here. After setup, import bank and card transactions from CSV.
        Automatic bank connection is not enabled in Version 1.
      </Callout>
      <Card>
        <h2>Add a manual account</h2>
        <form className="form-stack" onSubmit={submitAccount}>
          <div className="form-grid form-grid--two">
            <FormField
              label="Account name"
              htmlFor="account-name"
              error={formState.errors.name?.message}
            >
              <TextInput
                id="account-name"
                placeholder="Bills checking"
                {...register("name", { required: "Name the account." })}
              />
            </FormField>
            <FormField label="Account type" htmlFor="account-type">
              <SelectInput id="account-type" {...register("type")}>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
                <option value="credit">Credit card</option>
                <option value="loan">Loan</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </SelectInput>
            </FormField>
            <FormField
              label="Current balance"
              htmlFor="current-balance"
              hint="Use a minus sign for a negative cash balance."
            >
              <TextInput
                id="current-balance"
                inputMode="decimal"
                placeholder="0.00"
                {...register("currentBalance", { required: true })}
              />
            </FormField>
            <FormField
              label="Available balance"
              htmlFor="available-balance"
              hint="Optional when your bank does not show it."
            >
              <TextInput
                id="available-balance"
                inputMode="decimal"
                placeholder="0.00"
                {...register("availableBalance")}
              />
            </FormField>
            <FormField label="Protection" htmlFor="account-protection">
              <SelectInput id="account-protection" {...register("protectionLevel")}>
                <option value="none">Not protected</option>
                <option value="standard">Standard</option>
                <option value="protected">Protected</option>
                <option value="emergency_only">Emergency only</option>
              </SelectInput>
            </FormField>
          </div>
          {formError ? (
            <Callout title="Check the balance" tone="warning" role="alert">
              {formError}
            </Callout>
          ) : null}
          <SaveError error={createMutation.error} />
          <div className="button-row">
            <Button type="submit" isLoading={createMutation.isPending}>
              Add account
            </Button>
          </div>
        </form>
      </Card>
      <Card>
        <h2>Your accounts</h2>
        <QueryState
          isPending={accountsQuery.isPending}
          isError={accountsQuery.isError}
          error={accountsQuery.error}
          onRetry={() => void accountsQuery.refetch()}
          loadingTitle="Loading accounts"
        >
          {accounts.length === 0 ? (
            <StatePanel title="Add at least one account">
              A real briefing needs a current cash or liability account.
            </StatePanel>
          ) : (
            <div className="card-list">
              {accounts.map((account) => (
                <div className="list-row" key={account.id}>
                  <div className="list-row__main">
                    <strong>{account.name}</strong>
                    <span>{titleFromMachineValue(account.type)} · entered manually</span>
                  </div>
                  <div className="list-row__side">
                    <strong>{formatMoney(account.currentBalanceMinor, account.currency)}</strong>
                    <span className="small muted">
                      {titleFromMachineValue(account.protectionLevel)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </QueryState>
      </Card>
      <SaveError error={continueMutation.error} />
      <div className="button-row button-row--end">
        <ContinueButton
          isLoading={continueMutation.isPending}
          disabled={accounts.length === 0}
          onClick={() => continueMutation.mutate()}
        >
          Continue to purposes
        </ContinueButton>
      </div>
    </div>
  );
}

function PurposesStep() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const updateMutation = useMutation({
    mutationFn: (input: { accountId: string; purposeId: string; version: number }) =>
      api.updateAccount(input.accountId, { purposeId: input.purposeId }, input.version),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["accounts"] }),
  });
  const continueMutation = useMutation({
    mutationFn: () => api.saveOnboardingProgress({ step: "income", data: {} }),
    onSuccess: () => navigate("/onboarding/income"),
  });
  const accounts = accountsQuery.data?.data ?? [];
  const purposes = purposesQuery.data?.data ?? [];
  const isPending = accountsQuery.isPending || purposesQuery.isPending;
  const isError = accountsQuery.isError || purposesQuery.isError;
  const error = accountsQuery.error ?? purposesQuery.error;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Purpose before categories"
        title="What job does each account do?"
        description="An account can carry a primary purpose now; IterNest Money keeps purpose reservations separate from the bank balance."
      />
      <QueryState
        isPending={isPending}
        isError={isError}
        error={error}
        onRetry={() => {
          void accountsQuery.refetch();
          void purposesQuery.refetch();
        }}
      >
        <div className="card-list">
          {accounts.map((account) => (
            <Card key={account.id}>
              <div className="section-stack">
                <div>
                  <strong>{account.name}</strong>
                  <p className="muted small">
                    {formatMoney(account.currentBalanceMinor, account.currency)} current balance
                  </p>
                </div>
                <FormField label="Primary purpose" htmlFor={"purpose-" + account.id}>
                  <SelectInput
                    id={"purpose-" + account.id}
                    value={account.purposeId ?? ""}
                    onChange={(event) =>
                      updateMutation.mutate({
                        accountId: account.id,
                        purposeId: event.currentTarget.value,
                        version: account.version,
                      })
                    }
                  >
                    <option value="">Choose a purpose</option>
                    {purposes
                      .filter((purpose) => purpose.isActive)
                      .map((purpose) => (
                        <option value={purpose.id} key={purpose.id}>
                          {purpose.name}
                        </option>
                      ))}
                  </SelectInput>
                </FormField>
              </div>
            </Card>
          ))}
        </div>
      </QueryState>
      <SaveError error={updateMutation.error ?? continueMutation.error} />
      <div className="button-row button-row--end">
        <ContinueButton
          isLoading={continueMutation.isPending}
          disabled={accounts.some((account) => !account.purposeId)}
          onClick={() => continueMutation.mutate()}
        >
          Continue to income
        </ContinueButton>
      </div>
    </div>
  );
}

type IncomeForm = {
  name: string;
  type: IncomeSourceInput["type"];
  cadence: IncomeSourceInput["cadence"];
  expectedAmount: string;
  confidence: IncomeSourceInput["confidence"];
  landingAccountId: string;
};

function IncomeStep({ profile }: { profile: ProfileRead }) {
  const api = useApi();
  const navigate = useNavigate();
  const [hasAddedIncome, setHasAddedIncome] = useState(false);
  const [formError, setFormError] = useState<string>();
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const { register, handleSubmit, reset } = useForm<IncomeForm>({
    defaultValues: {
      type: "salary",
      cadence: "biweekly",
      expectedAmount: "",
      confidence: "confirmed",
      landingAccountId: "",
    },
  });
  const createMutation = useMutation({
    mutationFn: (input: IncomeSourceInput) => api.createIncomeSource(input),
    onSuccess: () => {
      setHasAddedIncome(true);
      reset();
    },
  });
  const continueMutation = useMutation({
    mutationFn: () => api.saveOnboardingProgress({ step: "obligations", data: {} }),
    onSuccess: () => navigate("/onboarding/obligations"),
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const expectedAmountMinor = values.expectedAmount
      ? parseMoneyInput(values.expectedAmount)
      : null;
    if (values.expectedAmount && (!expectedAmountMinor || expectedAmountMinor.startsWith("-"))) {
      setFormError("Enter expected take-home income as a positive amount.");
      return;
    }
    createMutation.mutate({
      name: values.name,
      type: values.type,
      cadence: values.cadence,
      ...(expectedAmountMinor ? { expectedAmountMinor } : {}),
      ...(values.landingAccountId ? { landingAccountId: values.landingAccountId } : {}),
      confidence: values.confidence,
    });
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Timing matters"
        title="Confirm how money arrives"
        description="Use take-home amounts. Estimates stay visibly estimated and never become cash coverage before they arrive."
      />
      {hasAddedIncome ? (
        <Callout title="Income source saved" tone="positive" role="status">
          Add another source or continue.
        </Callout>
      ) : null}
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField label="Income source" htmlFor="income-name">
              <TextInput
                id="income-name"
                placeholder="Primary paycheck"
                {...register("name", { required: true })}
              />
            </FormField>
            <FormField label="Type" htmlFor="income-type">
              <SelectInput id="income-type" {...register("type")}>
                <option value="salary">Salary</option>
                <option value="spark">Spark</option>
                <option value="bonus">Bonus</option>
                <option value="refund">Refund</option>
                <option value="other">Other</option>
              </SelectInput>
            </FormField>
            <FormField label="Cadence" htmlFor="income-cadence">
              <SelectInput id="income-cadence" {...register("cadence")}>
                <option value="biweekly">Every two weeks</option>
                <option value="weekly">Weekly</option>
                <option value="semimonthly">Twice monthly</option>
                <option value="monthly">Monthly</option>
                <option value="irregular">Irregular</option>
                <option value="manual">Manual</option>
              </SelectInput>
            </FormField>
            <FormField
              label={"Expected amount (" + profile.household.baseCurrency + ")"}
              htmlFor="income-amount"
              hint="Optional when timing or amount varies."
            >
              <TextInput
                id="income-amount"
                inputMode="decimal"
                placeholder="0.00"
                {...register("expectedAmount")}
              />
            </FormField>
            <FormField label="Confidence" htmlFor="income-confidence">
              <SelectInput id="income-confidence" {...register("confidence")}>
                <option value="confirmed">Confirmed</option>
                <option value="high">High confidence</option>
                <option value="medium">Estimate</option>
                <option value="low">Uncertain</option>
              </SelectInput>
            </FormField>
            <FormField label="Landing account" htmlFor="landing-account">
              <SelectInput id="landing-account" {...register("landingAccountId")}>
                <option value="">Not assigned</option>
                {(accountsQuery.data?.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          </div>
          {formError ? (
            <Callout title="Check the amount" tone="warning" role="alert">
              {formError}
            </Callout>
          ) : null}
          <SaveError error={createMutation.error} />
          <div className="button-row">
            <Button type="submit" isLoading={createMutation.isPending}>
              Add income source
            </Button>
          </div>
        </form>
      </Card>
      <SaveError error={continueMutation.error} />
      <div className="button-row button-row--end">
        <ContinueButton
          isLoading={continueMutation.isPending}
          onClick={() => continueMutation.mutate()}
        >
          Continue to obligations
        </ContinueButton>
      </div>
    </div>
  );
}

type ObligationForm = {
  name: string;
  type: ObligationInput["type"];
  frequency: ObligationInput["recurrence"]["frequency"];
  anchorDate: string;
  expectedAmount: string;
  purposeId: string;
  sourceAccountId: string;
  priority: string;
  flexibility: ObligationInput["flexibility"];
  splitAllowed: boolean;
};

function ObligationsStep({ profile }: { profile: ProfileRead }) {
  const api = useApi();
  const navigate = useNavigate();
  const [hasAdded, setHasAdded] = useState(false);
  const [formError, setFormError] = useState<string>();
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const { register, handleSubmit, reset } = useForm<ObligationForm>({
    defaultValues: {
      type: "bill",
      frequency: "monthly",
      anchorDate: new Date().toISOString().slice(0, 10),
      expectedAmount: "",
      purposeId: "",
      sourceAccountId: "",
      priority: "50",
      flexibility: "fixed",
      splitAllowed: true,
    },
  });
  const createMutation = useMutation({
    mutationFn: (input: ObligationInput) => api.createObligation(input),
    onSuccess: () => {
      setHasAdded(true);
      reset();
    },
  });
  const continueMutation = useMutation({
    mutationFn: () => api.saveOnboardingProgress({ step: "rules", data: {} }),
    onSuccess: () => navigate("/onboarding/rules"),
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const expectedAmountMinor = parseMoneyInput(values.expectedAmount);
    if (!expectedAmountMinor || expectedAmountMinor.startsWith("-") || !values.purposeId) {
      setFormError("Enter a positive amount and choose the purpose this obligation protects.");
      return;
    }
    createMutation.mutate({
      name: values.name,
      type: values.type,
      recurrence: { frequency: values.frequency, interval: 1, anchorDate: values.anchorDate },
      amountMethod: "user_confirmed",
      expectedAmountMinor,
      purposeId: values.purposeId,
      priority: Number(values.priority),
      flexibility: values.flexibility,
      ...(values.sourceAccountId ? { sourceAccountId: values.sourceAccountId } : {}),
      splitAllowed: values.splitAllowed,
      confidence: "confirmed",
    });
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Coverage starts here"
        title="What must the household protect?"
        description="Add bills, debt minimums, groceries, hockey or school commitments, and anything else the plan must reserve."
      />
      {hasAdded ? (
        <Callout title="Obligation saved" tone="positive" role="status">
          Add another obligation or continue.
        </Callout>
      ) : null}
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField label="Obligation name" htmlFor="obligation-name">
              <TextInput
                id="obligation-name"
                placeholder="Electric bill"
                {...register("name", { required: true })}
              />
            </FormField>
            <FormField label="Type" htmlFor="obligation-type">
              <SelectInput id="obligation-type" {...register("type")}>
                <option value="bill">Bill</option>
                <option value="debt_minimum">Debt minimum</option>
                <option value="groceries">Groceries</option>
                <option value="hockey_school">Hockey / school</option>
                <option value="subscription">Subscription</option>
                <option value="other">Other</option>
              </SelectInput>
            </FormField>
            <FormField label="How often" htmlFor="obligation-frequency">
              <SelectInput id="obligation-frequency" {...register("frequency")}>
                <option value="once">Once</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every two weeks</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </SelectInput>
            </FormField>
            <FormField label="Next due date" htmlFor="obligation-date">
              <TextInput
                id="obligation-date"
                type="date"
                {...register("anchorDate", { required: true })}
              />
            </FormField>
            <FormField
              label={"Expected amount (" + profile.household.baseCurrency + ")"}
              htmlFor="obligation-amount"
            >
              <TextInput
                id="obligation-amount"
                inputMode="decimal"
                placeholder="0.00"
                {...register("expectedAmount", { required: true })}
              />
            </FormField>
            <FormField label="Purpose protected" htmlFor="obligation-purpose">
              <SelectInput id="obligation-purpose" {...register("purposeId")}>
                <option value="">Choose a purpose</option>
                {(purposesQuery.data?.data ?? []).map((purpose) => (
                  <option key={purpose.id} value={purpose.id}>
                    {purpose.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField label="Usually paid from" htmlFor="obligation-account">
              <SelectInput id="obligation-account" {...register("sourceAccountId")}>
                <option value="">Choose later</option>
                {(accountsQuery.data?.data ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField label="Flexibility" htmlFor="obligation-flexibility">
              <SelectInput id="obligation-flexibility" {...register("flexibility")}>
                <option value="fixed">Fixed date</option>
                <option value="flexible">Flexible timing</option>
                <option value="pay_when_funded">Pay when funded</option>
              </SelectInput>
            </FormField>
          </div>
          <label className="check-row">
            <input type="checkbox" {...register("splitAllowed")} />
            <span>Allow this obligation to be funded across more than one income event.</span>
          </label>
          {formError ? (
            <Callout title="Check this obligation" tone="warning" role="alert">
              {formError}
            </Callout>
          ) : null}
          <SaveError error={createMutation.error} />
          <div className="button-row">
            <Button type="submit" isLoading={createMutation.isPending}>
              Add obligation
            </Button>
          </div>
        </form>
      </Card>
      <SaveError error={continueMutation.error} />
      <div className="button-row button-row--end">
        <ContinueButton
          isLoading={continueMutation.isPending}
          onClick={() => continueMutation.mutate()}
        >
          Continue to protection rules
        </ContinueButton>
      </div>
    </div>
  );
}

type RulesPayload = {
  readonly householdBufferMinor: string;
  readonly weeklyGroceriesMinor: string;
  readonly emergencyFloorMinor: string;
  readonly minimumActionMinor: string;
  readonly debtStrategy: "avalanche" | "snowball" | "custom";
  readonly protectedPriority: string;
};

function RulesStep() {
  const api = useApi();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit } = useForm<{
    householdBuffer: string;
    weeklyGroceries: string;
    emergencyFloor: string;
    minimumAction: string;
    debtStrategy: "avalanche" | "snowball" | "custom";
    priority: string;
  }>({
    defaultValues: {
      debtStrategy: "avalanche",
      priority: "essentials",
      householdBuffer: "",
      weeklyGroceries: "",
      emergencyFloor: "",
      minimumAction: "",
    },
  });
  const mutation = useMutation({
    mutationFn: async (data: RulesPayload) => {
      const [settingsResponse, purposesResponse] = await Promise.all([
        api.getSettings(),
        api.getPurposes(),
      ]);
      const settings = settingsResponse.data;
      await api.updateSettings(
        {
          household: {
            ...settings.household,
            householdBufferMinor: data.householdBufferMinor,
            minimumActionMinor: data.minimumActionMinor,
            debtStrategy: data.debtStrategy,
          },
        },
        settings.version,
      );

      const selectedType: PurposeRead["type"] =
        data.protectedPriority === "essentials"
          ? "bills"
          : (data.protectedPriority as PurposeRead["type"]);
      const floorPurposes: readonly {
        type: PurposeRead["type"];
        name: string;
        floor: string;
        defaultPriority: number;
      }[] = [
        {
          type: "groceries_free_money",
          name: "Groceries / free money",
          floor: data.weeklyGroceriesMinor,
          defaultPriority: 10,
        },
        {
          type: "emergency",
          name: "Emergency",
          floor: data.emergencyFloorMinor,
          defaultPriority: 20,
        },
      ];
      for (const desired of floorPurposes) {
        const existing = purposesResponse.data.find((purpose) => purpose.type === desired.type);
        const priority =
          desired.type === selectedType ? 1 : (existing?.priority ?? desired.defaultPriority);
        if (existing) {
          await api.updatePurpose(
            existing.id,
            {
              priority,
              protectionLevel: "protected",
              protectedFloorMinor: desired.floor,
              isActive: true,
            },
            existing.version,
          );
        } else {
          await api.createPurpose({
            name: desired.name,
            type: desired.type,
            priority,
            protectionLevel: "protected",
            protectedFloorMinor: desired.floor,
          });
        }
      }
      if (!floorPurposes.some((purpose) => purpose.type === selectedType)) {
        const selected = purposesResponse.data.find((purpose) => purpose.type === selectedType);
        if (selected) {
          await api.updatePurpose(
            selected.id,
            { priority: 1, protectionLevel: "protected", isActive: true },
            selected.version,
          );
        } else {
          await api.createPurpose({
            name: titleFromMachineValue(selectedType),
            type: selectedType,
            priority: 1,
            protectionLevel: "protected",
            protectedFloorMinor: "0",
          });
        }
      }
      return api.saveOnboardingProgress({ step: "spark", data: {} });
    },
    onSuccess: () => navigate("/onboarding/spark"),
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const householdBufferMinor = parseMoneyInput(values.householdBuffer || "0");
    const weeklyGroceriesMinor = parseMoneyInput(values.weeklyGroceries || "0");
    const emergencyFloorMinor = parseMoneyInput(values.emergencyFloor || "0");
    const minimumActionMinor = parseMoneyInput(values.minimumAction || "0");
    if (
      !householdBufferMinor ||
      !weeklyGroceriesMinor ||
      !emergencyFloorMinor ||
      !minimumActionMinor ||
      [householdBufferMinor, weeklyGroceriesMinor, emergencyFloorMinor, minimumActionMinor].some(
        (value) => value.startsWith("-"),
      )
    ) {
      setFormError("Protection amounts must be zero or positive.");
      return;
    }
    mutation.mutate({
      householdBufferMinor,
      weeklyGroceriesMinor,
      emergencyFloorMinor,
      minimumActionMinor,
      debtStrategy: values.debtStrategy,
      protectedPriority: values.priority,
    });
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Coverage before balance"
        title="Set the floors IterNest Money protects"
        description="These are editable household rules, not permanent commitments. Zero is acceptable when you are still building the plan."
      />
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField label="Minimum household buffer" htmlFor="household-buffer">
              <TextInput
                id="household-buffer"
                inputMode="decimal"
                placeholder="0.00"
                {...register("householdBuffer")}
              />
            </FormField>
            <FormField label="Weekly grocery reserve" htmlFor="grocery-reserve">
              <TextInput
                id="grocery-reserve"
                inputMode="decimal"
                placeholder="0.00"
                {...register("weeklyGroceries")}
              />
            </FormField>
            <FormField label="Emergency protected floor" htmlFor="emergency-floor">
              <TextInput
                id="emergency-floor"
                inputMode="decimal"
                placeholder="0.00"
                {...register("emergencyFloor")}
              />
            </FormField>
            <FormField
              label="Minimum action size"
              htmlFor="minimum-action"
              hint="IterNest Money will avoid low-value suggestions below this amount unless the issue is critical."
            >
              <TextInput
                id="minimum-action"
                inputMode="decimal"
                placeholder="0.00"
                {...register("minimumAction")}
              />
            </FormField>
            <FormField label="Debt strategy" htmlFor="debt-strategy">
              <SelectInput id="debt-strategy" {...register("debtStrategy")}>
                <option value="avalanche">Avalanche — highest APR first</option>
                <option value="snowball">Snowball — lowest balance first</option>
                <option value="custom">Custom order</option>
              </SelectInput>
            </FormField>
            <FormField label="First protected priority" htmlFor="protected-priority">
              <SelectInput id="protected-priority" {...register("priority")}>
                <option value="essentials">Bills and essentials</option>
                <option value="hockey_school">Hockey / school</option>
                <option value="emergency">Emergency floor</option>
                <option value="pressure_relief">Pressure relief</option>
              </SelectInput>
            </FormField>
          </div>
          {formError ? (
            <Callout title="Check the protection amounts" tone="warning" role="alert">
              {formError}
            </Callout>
          ) : null}
          <SaveError error={mutation.error} />
          <div className="button-row button-row--end">
            <ContinueButton isLoading={mutation.isPending} />
          </div>
        </form>
      </Card>
    </div>
  );
}

type SparkPlanningPayload = NonNullable<SettingsRead["sparkPlanning"]>;

function SparkStep() {
  const api = useApi();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit } = useForm<{
    minimumBuffer: string;
    comfortableIncrement: string;
    aggressiveIncrement: string;
    expectedNetPerDay: string;
    usualWorkdays: string[];
    tipsTreatment: "exclude_until_confirmed" | "count_when_confirmed";
    isConfigured: boolean;
  }>({
    defaultValues: {
      minimumBuffer: "",
      comfortableIncrement: "",
      aggressiveIncrement: "",
      expectedNetPerDay: "",
      usualWorkdays: ["mon", "tue", "wed", "thu", "fri"],
      tipsTreatment: "exclude_until_confirmed",
      isConfigured: true,
    },
  });
  const mutation = useMutation({
    mutationFn: async (data: SparkPlanningPayload) => {
      const settings = (await api.getSettings()).data;
      await api.updateSettings({ sparkPlanning: data }, settings.version);
      return api.saveOnboardingProgress({ step: "briefing", data: {} });
    },
    onSuccess: () => navigate("/onboarding/briefing"),
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const minimumBufferMinor = parseMoneyInput(values.minimumBuffer || "0");
    const comfortableIncrementMinor = parseMoneyInput(values.comfortableIncrement || "0");
    const aggressiveIncrementMinor = parseMoneyInput(values.aggressiveIncrement || "0");
    const expectedNetPerDayMinor = parseMoneyInput(values.expectedNetPerDay || "0");
    if (
      !minimumBufferMinor ||
      !comfortableIncrementMinor ||
      !aggressiveIncrementMinor ||
      !expectedNetPerDayMinor ||
      [
        minimumBufferMinor,
        comfortableIncrementMinor,
        aggressiveIncrementMinor,
        expectedNetPerDayMinor,
      ].some((value) => value.startsWith("-"))
    ) {
      setFormError("Spark amounts must be zero or positive.");
      return;
    }
    mutation.mutate({
      isConfigured: values.isConfigured,
      minimumBufferMinor,
      comfortableIncrementMinor,
      aggressiveIncrementMinor,
      expectedNetPerDayMinor,
      usualWorkdays: values.usualWorkdays as SparkPlanningPayload["usualWorkdays"],
      tipsTreatment: values.tipsTreatment,
    });
  });

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Pressure management, not judgment"
        title="Set your Spark working shape"
        description="IterNest Money will calculate the required amount from the household plan. Comfortable and aggressive goals remain optional."
      />
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <label className="check-row">
            <input type="checkbox" {...register("isConfigured")} />
            <span>Include Spark or other variable income in my plan</span>
          </label>
          <div className="form-grid form-grid--two">
            <FormField label="Spark cushion above required obligations" htmlFor="spark-buffer">
              <TextInput
                id="spark-buffer"
                inputMode="decimal"
                placeholder="0.00"
                {...register("minimumBuffer")}
              />
            </FormField>
            <FormField label="Comfortable goal increment" htmlFor="spark-comfortable">
              <TextInput
                id="spark-comfortable"
                inputMode="decimal"
                placeholder="0.00"
                {...register("comfortableIncrement")}
              />
            </FormField>
            <FormField label="Aggressive goal increment" htmlFor="spark-aggressive">
              <TextInput
                id="spark-aggressive"
                inputMode="decimal"
                placeholder="0.00"
                {...register("aggressiveIncrement")}
              />
            </FormField>
            <FormField
              label="Expected net per workday"
              htmlFor="spark-per-day"
              hint="Used only for an estimate of days needed."
            >
              <TextInput
                id="spark-per-day"
                inputMode="decimal"
                placeholder="0.00"
                {...register("expectedNetPerDay")}
              />
            </FormField>
          </div>
          <fieldset className="plain-fieldset">
            <legend>Usual workable days</legend>
            <div className="weekday-grid">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <label className="check-row" key={day}>
                  <input type="checkbox" value={day.toLowerCase()} {...register("usualWorkdays")} />
                  <span>{day}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <FormField label="Tips" htmlFor="tips-treatment">
            <SelectInput id="tips-treatment" {...register("tipsTreatment")}>
              <option value="exclude_until_confirmed">Exclude until confirmed</option>
              <option value="count_when_confirmed">Count after I confirm them</option>
            </SelectInput>
          </FormField>
          {formError ? (
            <Callout title="Check the Spark amounts" tone="warning" role="alert">
              {formError}
            </Callout>
          ) : null}
          <SaveError error={mutation.error} />
          <div className="button-row button-row--end">
            <ContinueButton isLoading={mutation.isPending} />
          </div>
        </form>
      </Card>
    </div>
  );
}

function BriefingStep({ profile }: { profile: ProfileRead }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const suggestedTimeZone = onboardingTimeZone(profile.timezone);
  const needsTimeZoneRepair = !isIanaTimeZone(profile.timezone);
  const briefingQuery = useQuery({
    queryKey: ["briefing", "first"],
    queryFn: () => api.getBriefing(),
    refetchInterval: (query) => (query.state.data?.data ? false : 2_000),
  });
  const completeMutation = useMutation({
    mutationFn: () =>
      api.saveOnboardingProgress({
        step: "complete",
        data: { completedAt: new Date().toISOString() },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate("/home", { replace: true });
    },
  });
  const calculationRecoveryMutation = useMutation({
    mutationFn: async () => {
      if (needsTimeZoneRepair) {
        await api.updateMe({ timezone: suggestedTimeZone });
      }
      const currentSettings = await api.getSettings();
      return api.updateSettings(
        { household: currentSettings.data.household },
        currentSettings.data.version,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await briefingQuery.refetch();
    },
  });
  const briefing = briefingQuery.data?.data;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="One coherent plan"
        title="Your first briefing"
        description="This briefing uses only saved household information. Unknowns stay visible, and uncertain money is not labeled safe to use."
      />
      <QueryState
        isPending={briefingQuery.isPending}
        isError={briefingQuery.isError}
        error={briefingQuery.error}
        onRetry={() => void briefingQuery.refetch()}
        loadingTitle="Calculating your first briefing"
      >
        {briefing ? (
          <>
            {briefing.freshness.isStale ? (
              <Callout title="Some information is stale" tone="warning">
                IterNest Money is showing the latest trustworthy calculation with limitations.
              </Callout>
            ) : null}
            <Card className="hero-card">
              <p className="mm-eyebrow">Today’s action</p>
              <h2>{briefing.todayAction.action}</h2>
              <p className="hero-card__reason">{briefing.todayAction.whyNow}</p>
              <p className="hero-card__protects">
                Protects: {briefing.todayAction.protects.join(", ") || "the current covered plan"}
              </p>
            </Card>
            <Card>
              <div className="summary-grid">
                <div className="summary-tile">
                  <span>Safe to use through this horizon</span>
                  <strong>
                    {formatMoney(briefing.displaySafeToUseMinor, profile.household.baseCurrency)}
                  </strong>
                </div>
                <div className="summary-tile">
                  <span>Coverage confidence</span>
                  <strong>{titleFromMachineValue(briefing.freshness.confidence)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Needs attention</span>
                  <strong>{briefing.needsAttention.length}</strong>
                </div>
              </div>
              {briefing.limitations.length > 0 ? (
                <div className="section-stack">
                  <h3>Before relying on safe-to-use</h3>
                  <ul>
                    {briefing.limitations.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p>
                  <StatusBadge status="covered" /> Critical setup is complete for this calculation.
                </p>
              )}
            </Card>
          </>
        ) : needsTimeZoneRepair ? (
          <Callout title="Update your household timezone" tone="warning" role="alert">
            <p>
              Your setup saved an older fixed-offset timezone ({profile.timezone}). Update it to{" "}
              {suggestedTimeZone} so IterNest Money can calculate due dates and prepare your
              briefing.
            </p>
            <Button
              type="button"
              isLoading={calculationRecoveryMutation.isPending}
              onClick={() => calculationRecoveryMutation.mutate()}
            >
              Update timezone and recalculate
            </Button>
          </Callout>
        ) : (
          <StatePanel title="Preparing your first briefing" kind="loading">
            <p>
              IterNest Money has saved your setup and is calculating coverage. This page will update
              automatically when the briefing is ready.
            </p>
            <Button
              type="button"
              isLoading={calculationRecoveryMutation.isPending}
              onClick={() => calculationRecoveryMutation.mutate()}
            >
              Request a fresh calculation
            </Button>
          </StatePanel>
        )}
      </QueryState>
      <SaveError error={calculationRecoveryMutation.error} />
      <SaveError error={completeMutation.error} />
      <div className="button-row button-row--end">
        <Button
          type="button"
          isLoading={completeMutation.isPending}
          onClick={() => completeMutation.mutate()}
          disabled={!briefing}
        >
          Open IterNest Money
        </Button>
      </div>
    </div>
  );
}

export function OnboardingPage() {
  const { step: stepParam } = useParams<{ step: string }>();
  const api = useApi();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: () => api.getMe() });
  const step = isOnboardingStep(stepParam) ? stepParam : null;
  const profile = meQuery.data?.data;
  const content = useMemo(() => {
    if (!step || !profile) return null;
    switch (step) {
      case "profile":
        return <ProfileStep profile={profile} />;
      case "accounts":
        return <AccountsStep profile={profile} />;
      case "purposes":
        return <PurposesStep />;
      case "income":
        return <IncomeStep profile={profile} />;
      case "obligations":
        return <ObligationsStep profile={profile} />;
      case "rules":
        return <RulesStep />;
      case "spark":
        return <SparkStep />;
      case "briefing":
        return <BriefingStep profile={profile} />;
    }
  }, [step, profile]);

  if (!step) return <Navigate to="/onboarding/profile" replace />;
  return (
    <OnboardingLayout step={step}>
      <QueryState
        isPending={meQuery.isPending}
        isError={meQuery.isError}
        error={meQuery.error}
        onRetry={() => void meQuery.refetch()}
        loadingTitle="Resuming setup"
      >
        {content}
      </QueryState>
    </OnboardingLayout>
  );
}
